import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { IconButton, InputAdornment, TextField, type TextFieldProps } from '@mui/material';

type PasswordInputProps = Omit<TextFieldProps, 'type'> & {
  showStrength?: boolean;
};

export function PasswordInput({
  showStrength = true,
  helperText,
  value,
  ...textFieldProps
}: PasswordInputProps) {
  const { t } = useTranslation('idp');
  const [isVisible, setIsVisible] = useState(false);

  const passwordLength = useMemo(() => {
    if (typeof value === 'string') {
      return value.length;
    }

    return 0;
  }, [value]);

  const computedHelperText = useMemo(() => {
    if (!showStrength) {
      return helperText;
    }

    const lengthText = `${passwordLength}/12 characters`;
    if (helperText === undefined || helperText === null || typeof helperText !== 'string') {
      return helperText ?? lengthText;
    }

    return `${helperText} · ${lengthText}`;
  }, [helperText, passwordLength, showStrength]);

  return (
    <TextField
      {...textFieldProps}
      type={isVisible ? 'text' : 'password'}
      value={value}
      helperText={computedHelperText}
      InputProps={{
        ...(textFieldProps.InputProps ?? {}),
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={
                isVisible ? t('passwordInput.hidePassword') : t('passwordInput.showPassword')
              }
              edge="end"
              onClick={() => {
                setIsVisible((current) => !current);
              }}
            >
              {isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
}
